import { StockAllocationRow } from "features/iot/stock-management/models/stock-management-models"
import { getAllocatedSensors } from "../stock-managment/stock-management-helper"
import { SDASensor } from "features/iot/stock-management/models/sensor-dispatch-allocation"
import customerStore from "features/organizations/customers/store/customer-store"
import { convertDateToDDMMMYYYY } from "components/utils/date-formatter-utils"

export function groupSensorsForStockAllocation(partNumber: string): StockAllocationRow[] {
    if (!partNumber) return []

    const sensors = getAllocatedSensors(partNumber)
    if (sensors.length === 0) return []

    let allocatedSensors = aggregateSensorsToStockAllocation(sensors)
    return sortStockAllocations(allocatedSensors)
}

/**
 * Maps and aggregates sensors within batches to StockAllocationRow format.
 */
function aggregateSensorsToStockAllocation(sensors: SDASensor[]): StockAllocationRow[] {
    const allocationsMap: Record<string, StockAllocationRow> = {}

    sensors.forEach(sensor => {
        const customerName: string = (sensor.customerId && customerStore.customerMap[sensor.customerId]?.name) || "unknown"
        const siteName: string = (sensor.siteId && customerStore.siteMap[sensor.siteId]?.name) || "unknown"
        const allocationDate: string = convertDateToDDMMMYYYY(sensor.allocationDate ?? null)

        if (sensor.allocated && !sensor.dispatched) {
            const key = `${customerName}-${siteName}-${allocationDate}`

            // Initialize StockAllocationRow if key does not exist
            if (!allocationsMap[key]) {
                allocationsMap[key] = {
                    id: sensor.macAddress, // or another unique identifier if needed
                    customerName: customerName,
                    siteName: siteName,
                    date: allocationDate,
                    quantity: 0,
                    customerId: sensor.customerId || "",
                    siteId: sensor.siteId,
                }
            }

            // Increment the quantity for sensors with the same customer, site, and date
            allocationsMap[key].quantity += 1
        }
    })

    return Object.values(allocationsMap)
}

/**
 * Sorts an array of stock allocation rows primarily by customer name, then by site name.
 * Null values for customers or sites are sorted to the end of the array. This function
 * ensures that the final list of allocations is presented in a consistent and predictable order.
 *
 * @param {StockAllocationRow[]} allocations - The array of stock allocation rows to be sorted.
 * @returns {StockAllocationRow[]} - The sorted array of stock allocation rows.
 */
function sortStockAllocations(allocations: StockAllocationRow[]): StockAllocationRow[] {
    return allocations.sort((a, b) => {
        // Sorting by customer name, nulls last
        if (!a.customerName) return 1
        if (!b.customerName) return -1
        if (a.customerName < b.customerName) return -1
        if (a.customerName > b.customerName) return 1

        // If customers are equal, then sort by site, nulls last
        if (!a.siteName) return 1
        if (!b.siteName) return -1
        if (a.siteName < b.siteName) return -1
        if (a.siteName > b.siteName) return 1

        return 0 // Customers and sites are equal
    })
}

/**
 * Normalizes the site name by converting "unknown" or empty values to `null`.
 * This function is designed to ensure that site names with the specific case-insensitive string "unknown",
 * as well as null or undefined values, are treated uniformly as `null` in the application's data processing logic.
 * This approach can be particularly useful for data cleaning and preparation before saving or further processing.
 *
 * @param {string | null | undefined} siteName - The site name to be checked and normalized. The value can be a string,
 *                                                including the case-insensitive string "unknown", or `null`/`undefined`
 *                                                to indicate that the site name is not specified.
 * @returns {string | null} - Returns the original `siteName` if it is neither "unknown" nor an empty value (including
 *                            `null` or `undefined`). Returns `null` if `siteName` is "unknown", an empty string, `null`,
 *                            or `undefined`, to signify the absence or irrelevance of the site name in the given context.
 *
 * Example usage:
 * const site = checkSiteName("Unknown"); // returns null
 * const site = checkSiteName(""); // returns null
 * const site = checkSiteName(null); // returns null
 * const site = checkSiteName("Main Site"); // returns "Main Site"
 */
export function convertUnknownSiteNameToUndefined(siteName: string | undefined): string | undefined {
    if (siteName?.toLocaleLowerCase() === "unknown" || !siteName) {
        return undefined
    }
    return siteName
}
